import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import GlobalThrobberComponent from '../components/global_throbber_component';
import { useNotification } from '../components/global_noification_component';
import '../css/discord_application_page.css';
const API_BASE = process.env.REACT_APP_API_BASE
const API_VERSION = process.env.REACT_APP_API_ENVIRONMENT
const OAUTH_PAGE = process.env.REACT_APP_API_DISCORD_OAUTH_PAGE;

const DiscordContentCreatorApplicationPage = () => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [platform, setPlatform] = useState(sessionStorage.getItem('cc_platform') || '');
  let [link, setLink] = useState(sessionStorage.getItem('cc_link') || '');
  const [errors, setErrors] = useState({});
  const [user, setUser] = useState(null);
  const [eligible, setEligible] = useState(false);
  const lockedUntilCookie = Number(Cookies.get('cc_application_locked_until') || 0);
  const [lockedUntil, setLockedUntil] = useState(lockedUntilCookie);
  const [blocked, setBlocked] = useState(lockedUntil > Date.now());
  const [remaining, setRemaining] = useState(lockedUntil > Date.now() ? lockedUntil - Date.now() : 0);

  const submittedRef = useRef(false);

  const regexes = {
    TikTok: /^https:\/\/(www\.|m\.)?tiktok\.com\/(@[\w.-]+|[\w.-]+)$/i,
    YouTube: /^https:\/\/(www\.)?youtube\.com\/(@\w+|[\w-]+)$/i,
    'IG Reels': /^https:\/\/(www\.)?instagram\.com\/[\w-]+$/i
  };

  const cleanInput = (str) => str.replace(/`+/g, '').trim();

  useEffect(() => {
    (async () => {
      const code = new URLSearchParams(window.location.search).get('code');
      let data;
      const stored = sessionStorage.getItem('discordUser');
      if (stored) {
        data = JSON.parse(stored);
      } else if (code) {
        try {
          const res = await fetch(`${API_BASE}/${API_VERSION}/public/oAuth/discord?code=${code}`);
          const result = await res.json();
          if (!res.ok || !result.success) {
            notify('error', result.data?.message || 'Authentication failed');
            window.location.href = OAUTH_PAGE;
            return;
          }
          data = result.data;
          sessionStorage.setItem('discordUser', JSON.stringify(data));
        } catch {
          notify('error', 'Error fetching Discord user info');
          setLoading(false);
          navigate('/');
          return;
        }
      } else {
        if (!Cookies.get('refresh_token')) {
          window.location.href = OAUTH_PAGE;
          return;
        }
      }
      setUser(data);
      try {
        const res2 = await fetch(`${API_BASE}/${API_VERSION}/public/oAuth/discord/fetch`, {
          method: 'POST',
          headers: { 'Content-Type':'application/json' },
          body: JSON.stringify({
            code: Cookies.get('refresh_token'),
            application: 'content_creator',
          })
        });
        const check = await res2.json();
        setUser(check.user);
        Cookies.set('discord_id', check.user.id, { sameSite: 'strict' });
        Cookies.set('username', check.user.username, { sameSite: 'strict' });
        Cookies.set('global_name', check.user.global_name, { sameSite: 'strict' });
        Cookies.set('avatar', check.user.avatar, { sameSite: 'strict' });
        Cookies.set('banner', check.user.banner, { sameSite: 'strict' });
        Cookies.set('email', check.user.email, { sameSite: 'strict' });
        Cookies.set('code', check.code, { sameSite: 'strict' });
        Cookies.set('refresh_token', check.refresh_token, { sameSite: 'strict' });
        setEligible(check.is_in_gym_guild && (check.is_moderator || check.has_required_role));
        if (!check.is_in_gym_guild || (!check.is_moderator && !check.has_required_role)) {
        }
        if (blocked && remaining < 120000) {
          notify('info', 'Hang tight — you can resubmit in under 2 minutes.');
        }
      } catch {
        notify('error', 'Failed verifying eligibility.');
        setLoading(false);
        navigate('/');
        return;
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!blocked) return;
    const timer = setInterval(() => {
      const diff = lockedUntil - Date.now();
      if (diff <= 0) {
        clearInterval(timer);
        Cookies.remove('cc_application_locked_until');
        setBlocked(false);
        setRemaining(0);
      } else {
        setRemaining(diff);
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [blocked, lockedUntil]);

  useEffect(() => {
    sessionStorage.setItem('cc_platform', platform);
    sessionStorage.setItem('cc_link', link);
  }, [platform, link]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blocked) {
      notify('error', 'You must wait before submitting another application.');
      return;
    }
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    const errs = {};
    if (!platform) errs.platform = 'Please select a platform';
    if (!link) {
      link = cleanInput(link);
      errs.link = 'Please enter your social link';
    } else {
      link = cleanInput(link);
      if (platform && !regexes[platform].test(link)) {
        errs.link = `Please enter a valid ${platform} link`;
      }
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      Object.values(errs).forEach(msg => notify('error', msg));
      return;
    }
    setLoading(true);
    try {
      const payload = { platform, contentLink: link, discordUser: user };
      const res = await fetch(`${API_BASE}/${API_VERSION}/public/applications/content-creator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 429) {
        const data = await res.json();
        notify('error', data.message || 'Duplicate submission detected. Please wait before reapplying.');
        const until = Date.now() + 24 * 60 * 60 * 1000;
        Cookies.set('cc_application_locked_until', String(until), { expires: 1, sameSite: 'strict' });
        setLockedUntil(until);
        setBlocked(true);
        setRemaining(24 * 60 * 60 * 1000);
        setLoading(false);
        navigate('/');
        return;
      }
      if (res.status === 400) {
        const data = await res.json();
        notify('error', data.message || 'Profile validation failed.');
        setLoading(false);
        return;
      }
      if (res.ok) {
        notify('success', 'Application submitted successfully!');
        sessionStorage.removeItem('cc_platform');
        sessionStorage.removeItem('cc_link');
        navigate('/');
      } else {
        const text = await res.text();
        notify('error', `Submission failed: ${text}`);
        setLoading(false);
        navigate('/');
        return;
      }
    } catch (e) {
      notify('error', 'Error submitting application');
      setLoading(false);
      navigate('/');
      return;
    } finally {
        setLoading(false);
    }
    return;
  };

  if (loading || !user) {
    return <GlobalThrobberComponent />;
  }

  if (!eligible) {
    return (
      <div className="application-page content-creator">
        <p>You are not yet eligible to submit this application.</p>
      </div>
    );
  }

  if (blocked) {
    return (
      <div className="application-page content-creator">
        <p>
          You have recently submitted an application. Please wait&nbsp;
          {Math.ceil(remaining / 60000)} more minutes before submitting again.
        </p>
      </div>
    );
  }

  return (
    <div className="application-page content-creator">
      <form className="application-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <h1>
            Content Creator Application
          </h1>
          <label htmlFor="platform">Platform</label>
          <select id="platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="">-- Select Platform --</option>
            <option value="TikTok">TikTok</option>
            <option value="YouTube">YouTube</option>
            <option value="IG Reels">IG Reels</option>
          </select>
        </div>
        {errors.platform && <div className="error">{errors.platform}</div>}
        <div className="form-group">
          <label htmlFor="link">Social Link</label>
          <input id="link" type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." />
        </div>
        {errors.link && <div className="error">{errors.link}</div>}
        <button type='submit' className='submit-button' disabled={submitting || loading}>
          Submit Application
        </button>
      </form>
    </div>
  );
};

export default DiscordContentCreatorApplicationPage;
